import {Alert, Button, Group, MultiSelect, Pagination, Paper, Progress, Select, Stack, Table, Text, TextInput, Title} from "@mantine/core";
import {useMutation, useQuery} from "@tanstack/react-query";
import {DateTime} from "luxon";
import {useContext, useEffect, useMemo, useState} from "react";
import {useParams} from "react-router";
import withRouteErrorBoundary from "../../../components/ErrorBoundary/withRouteErrorBoundary.tsx";
import LoaderScreen from "../../../components/Loader/LoaderScreen.tsx";
import Page from "../../../components/Page/Page.tsx";
import MessageContext from "../../../context/MessageContext.tsx";
import api from "../../../utils/api.ts";
import ProductionModuleNav from "./ProductionModuleNav.tsx";
import {useProductionModule} from "./ProductionModuleContext.tsx";
import {
    ProductionGroup,
    ProductionManagementResponse,
    ProductionTrackedEntity,
} from "./types.ts";
import {WarningIcon} from "@phosphor-icons/react";



interface BulkRefreshState {
    mode: 'station' | 'facility' | 'materials';
    total: number;
    done: number;
    failed: number;
    running: boolean;
}

type DiscoverMode = 'all' | 'tag' | 'infotext';
const TRACKED_ENTITIES_PAGE_SIZE = 20;

function parseIds(value: string): string[] {
    return value
        .split(/[\s,]+/)
        .map((item) => item.trim())
        .filter(Boolean);
}

function ProductionModuleEntitiesPage() {
    const {moduleId} = useParams<{moduleId?: string}>();
    const moduleInfo = useProductionModule();
    const {success, error} = useContext(MessageContext);

    const [stationIds, setStationIds] = useState('');
    const [facilityIds, setFacilityIds] = useState('');
    const [discoverMode, setDiscoverMode] = useState<DiscoverMode>('all');
    const [discoverValue, setDiscoverValue] = useState('');
    const [groupName, setGroupName] = useState('');
    const [groupEntityIds, setGroupEntityIds] = useState<string[]>([]);
    const [groupCargoEntityIds, setGroupCargoEntityIds] = useState<string[]>([]);
    const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
    const [editGroupName, setEditGroupName] = useState('');
    const [editGroupEntityIds, setEditGroupEntityIds] = useState<string[]>([]);
    const [editGroupCargoEntityIds, setEditGroupCargoEntityIds] = useState<string[]>([]);
    const [bulkRefresh, setBulkRefresh] = useState<BulkRefreshState | null>(null);
    const [trackedEntitiesPage, setTrackedEntitiesPage] = useState(1);

    const managementQuery = useQuery<ProductionManagementResponse>({
        queryKey: ['prod-module', moduleId, 'management'],
        queryFn: async () => (await api.get(`/modules/production/${moduleId}/management`)).data as ProductionManagementResponse,
        enabled: !!moduleId,
    });


    const refetchManagement = () => managementQuery.refetch();

    const importStationsMutation = useMutation({
        mutationFn: async () => {
            return (await api.post(`/modules/production/${moduleId}/entities/import/stations`, {ids: parseIds(stationIds)})).data;
        },
        onSuccess: (data: any) => {
            success('Station import complete', `Added ${data.added}, updated ${data.updated}, errors ${data.errors}`);
            setStationIds('');
            refetchManagement();
        },
        onError: (e: any) => error('Station import failed', e?.response?.data?.error ?? e.message),
    });

    const importFacilitiesMutation = useMutation({
        mutationFn: async () => {
            return (await api.post(`/modules/production/${moduleId}/entities/import/facilities`, {ids: parseIds(facilityIds)})).data;
        },
        onSuccess: (data: any) => {
            success('Facility import complete', `Added ${data.added}, updated ${data.updated}, errors ${data.errors}`);
            setFacilityIds('');
            refetchManagement();
        },
        onError: (e: any) => error('Facility import failed', e?.response?.data?.error ?? e.message),
    });

    const discoverPayload = () => ({
        mode: discoverMode,
        value: discoverMode === 'all' ? undefined : (discoverValue.trim() || undefined),
    });

    const discoverStationsMutation = useMutation({
        mutationFn: async () => {
            return (await api.post(`/modules/production/${moduleId}/entities/discover/stations`, discoverPayload())).data;
        },
        onSuccess: (data: any) => {
            success('Station discovery import complete', `Discovered ${data.discovered}, added ${data.added}, updated ${data.updated}, errors ${data.errors}`);
            refetchManagement();
        },
        onError: (e: any) => error('Station discovery import failed', e?.response?.data?.error ?? e.message),
    });

    const discoverFacilitiesMutation = useMutation({
        mutationFn: async () => {
            return (await api.post(`/modules/production/${moduleId}/entities/discover/facilities`, discoverPayload())).data;
        },
        onSuccess: (data: any) => {
            success('Facility discovery import complete', `Discovered ${data.discovered}, added ${data.added}, updated ${data.updated}, errors ${data.errors}`);
            refetchManagement();
        },
        onError: (e: any) => error('Facility discovery import failed', e?.response?.data?.error ?? e.message),
    });

    const refreshMutation = useMutation({
        mutationFn: async (row: ProductionTrackedEntity) => {
            return (await api.post(`/modules/production/${moduleId}/entities/${row.entityId}/refresh`, {entityType: row.entityType})).data;
        },
        onSuccess: () => {
            success('Entity refreshed');
            refetchManagement();
        },
        onError: (e: any) => error('Refresh failed', e?.response?.data?.error ?? e.message),
    });

    const deleteEntityMutation = useMutation({
        mutationFn: async (row: ProductionTrackedEntity) => {
            return (await api.delete(`/modules/production/${moduleId}/entities/${row.entityId}`)).data;
        },
        onSuccess: () => {
            success('Entity removed');
            refetchManagement();
        },
        onError: (e: any) => error('Delete failed', e?.response?.data?.error ?? e.message),
    });

    const createGroupMutation = useMutation({
        mutationFn: async () => {
            return (await api.post(`/modules/production/${moduleId}/groups`, {
                name: groupName,
                entityIds: groupEntityIds,
                cargoEntityIds: groupCargoEntityIds,
            })).data;
        },
        onSuccess: () => {
            success('Group created');
            setGroupName('');
            setGroupEntityIds([]);
            setGroupCargoEntityIds([]);
            refetchManagement();
        },
        onError: (e: any) => error('Could not create group', e?.response?.data?.error ?? e.message),
    });

    const updateGroupMutation = useMutation({
        mutationFn: async () => {
            if (!editingGroupId) {
                return null;
            }

            return (await api.post(`/modules/production/${moduleId}/groups/${editingGroupId}`, {
                name: editGroupName,
                entityIds: editGroupEntityIds,
                cargoEntityIds: editGroupCargoEntityIds,
            })).data;
        },
        onSuccess: () => {
            success('Group updated');
            setEditingGroupId(null);
            setEditGroupName('');
            setEditGroupEntityIds([]);
            setEditGroupCargoEntityIds([]);
            refetchManagement();
        },
        onError: (e: any) => error('Could not update group', e?.response?.data?.error ?? e.message),
    });

    const deleteGroupMutation = useMutation({
        mutationFn: async (groupId: string) => (await api.delete(`/modules/production/${moduleId}/groups/${groupId}`)).data,
        onSuccess: () => {
            success('Group removed');
            refetchManagement();
        },
        onError: (e: any) => error('Could not remove group', e?.response?.data?.error ?? e.message),
    });

    const moduleData = managementQuery.data?.module;
    const entities = managementQuery.data?.entities ?? [];
    const groups = managementQuery.data?.groups ?? [];
    const trackedEntitiesTotalPages = Math.max(1, Math.ceil(entities.length / TRACKED_ENTITIES_PAGE_SIZE));
    const paginatedEntities = useMemo(() => {
        const startIndex = (trackedEntitiesPage - 1) * TRACKED_ENTITIES_PAGE_SIZE;
        return entities.slice(startIndex, startIndex + TRACKED_ENTITIES_PAGE_SIZE);
    }, [entities, trackedEntitiesPage]);


    const trackedEntityOptions = useMemo(() => {
        return entities.map((entity) => ({
            value: entity.entityId,
            label: `${entity.name} (${entity.entityType})`,
        }));
    }, [entities]);

    useEffect(() => {
        if (!editingGroupId) {
            return;
        }

        const editingGroup = groups.find((group) => group.id === editingGroupId);
        if (!editingGroup) {
            setEditingGroupId(null);
            setEditGroupName('');
            setEditGroupEntityIds([]);
            setEditGroupCargoEntityIds([]);
        }
    }, [editingGroupId, groups]);

    useEffect(() => {
        setTrackedEntitiesPage((current) => Math.min(current, trackedEntitiesTotalPages));
    }, [trackedEntitiesTotalPages]);

    if (!moduleId) {
        return <Page><Alert color="red" title="Missing module ID">No module selected.</Alert></Page>;
    }
    if (managementQuery.isPending) {
        return <LoaderScreen message="Loading management data"/>;
    }
    if (managementQuery.isError || !managementQuery.data) {
        return <Page><Alert color="red" title="Management unavailable">Could not load management data.</Alert></Page>;
    }

    const startEditingGroup = (group: ProductionGroup) => {
        setEditingGroupId(group.id);
        setEditGroupName(group.name);
        setEditGroupEntityIds(group.entityIds);
        setEditGroupCargoEntityIds(group.cargoEntityIds ?? []);
    };

    const stopEditingGroup = () => {
        setEditingGroupId(null);
        setEditGroupName('');
        setEditGroupEntityIds([]);
        setEditGroupCargoEntityIds([]);
    };

    const runBulkRefresh = async (mode: 'station' | 'facility' | 'materials') => {
        if (mode === 'materials') {
            setBulkRefresh({
                mode,
                total: 1,
                done: 0,
                failed: 0,
                running: true,
            });

            try {
                const response = await api.post(`/modules/production/${moduleId}/materials/refresh`);
                const payload = response.data as {materials?: number; containers?: number; sourceRows?: number};
                setBulkRefresh({
                    mode,
                    total: 1,
                    done: 1,
                    failed: 0,
                    running: false,
                });

                await refetchManagement();

                success(
                    'All materials refreshed',
                    `Imported ${payload.materials ?? 0} material stacks across ${payload.containers ?? 0} containers from ${payload.sourceRows ?? 0} source rows.`
                );
            } catch {
                setBulkRefresh({
                    mode,
                    total: 1,
                    done: 1,
                    failed: 1,
                    running: false,
                });
                await refetchManagement();
                error('Material refresh completed with errors', 'The owner-material sync request failed.');
            }

            return;
        }

        const targetEntities = entities.filter((entity) => entity.entityType === mode);
        if (targetEntities.length === 0) {
            success('No stations to refresh', 'There are no tracked stations.');
            return;
        }

        setBulkRefresh({
            mode,
            total: targetEntities.length,
            done: 0,
            failed: 0,
            running: true,
        });

        let done = 0;
        let failed = 0;

        for (const entity of targetEntities) {
            try {
                await api.post(`/modules/production/${moduleId}/entities/${entity.entityId}/refresh`, {entityType: entity.entityType});
            } catch {
                failed += 1;
            } finally {
                done += 1;
                setBulkRefresh({
                    mode,
                    total: targetEntities.length,
                    done,
                    failed,
                    running: true,
                });
            }
        }

        setBulkRefresh({
            mode,
            total: targetEntities.length,
            done,
            failed,
            running: false,
        });

        await refetchManagement();

        if (failed > 0) {
            error(
                'Station refresh completed with errors',
                `Refreshed ${done - failed} of ${done} entities successfully.`
            );
        } else {
            success(
                'All stations refreshed',
                `Refreshed ${done} out of ${done} entities.`
            );
        }
    };

    const hasUserSync = moduleData && (moduleData.ownerType === 'user' || moduleData.settings['syncUserId'] !== null);

    const bulkRefreshPercent = bulkRefresh ? Math.round((bulkRefresh.done / Math.max(1, bulkRefresh.total)) * 100) : 0;

    return (
        <Page>
            <title>{`${moduleInfo?.label || 'Production'} - Entities`}</title>
            <Stack gap="md">
                <Title order={1}>{moduleInfo?.label || 'Production'} - Entities</Title>
                <ProductionModuleNav moduleId={moduleId}/>

                <Stack gap="md">

                <Paper withBorder p="md" style={{order: 2}}>
                    <Group justify="space-between" align="end" mb="sm">
                        <Text fw={700}>Tracked Entities</Text>
                        <Group gap="xs">
                            <Button
                              size="compact-sm"
                              variant="light"
                              loading={bulkRefresh?.running && bulkRefresh.mode === 'facility'}
                              disabled={!!bulkRefresh?.running || !hasUserSync}
                              onClick={() => runBulkRefresh('facility')}
                            >
                                Refresh All Facilities
                            </Button>
                            <Button
                                size="compact-sm"
                                variant="light"
                                loading={bulkRefresh?.running && bulkRefresh.mode === 'station'}
                                disabled={!!bulkRefresh?.running || !hasUserSync}
                                onClick={() => runBulkRefresh('station')}
                            >
                                Refresh All Stations
                            </Button>
                            <Button
                                size="compact-sm"
                                variant="light"
                                loading={bulkRefresh?.running && bulkRefresh.mode === 'materials'}
                                disabled={!!bulkRefresh?.running || !hasUserSync}
                                onClick={() => runBulkRefresh('materials')}
                            >
                                Refresh All Materials
                            </Button>
                        </Group>
                    </Group>
                    {bulkRefresh ? (
                        <Stack gap={6} mb="sm">
                            <Text size="sm" c="dimmed">
                                {bulkRefresh.mode === 'station'
                                    ? `Refreshing stations: Refreshed ${bulkRefresh.done} out of ${bulkRefresh.total} entities`
                                    : `Refreshing materials: Completed ${bulkRefresh.done} out of ${bulkRefresh.total} requests`}
                            </Text>
                            <Progress value={bulkRefreshPercent} animated={bulkRefresh.running}/>
                            {!bulkRefresh.running && bulkRefresh.failed > 0 ? (
                                <Text size="sm" c="orange">
                                    {bulkRefresh.mode === 'station' ? 'Failed entities' : 'Failed requests'}: {bulkRefresh.failed}
                                </Text>
                            ) : null}
                        </Stack>
                    ) : null}
                    {entities.length > 0 ? (
                        <Group justify="space-between" align="center" mt="sm">
                            <Text size="sm" c="dimmed">
                                Showing {(trackedEntitiesPage - 1) * TRACKED_ENTITIES_PAGE_SIZE + 1}
                                {" - "}
                                {Math.min(trackedEntitiesPage * TRACKED_ENTITIES_PAGE_SIZE, entities.length)}
                                {" of "}
                                {entities.length}
                            </Text>
                            <Pagination
                                value={trackedEntitiesPage}
                                onChange={setTrackedEntitiesPage}
                                total={trackedEntitiesTotalPages}
                                size="sm"
                            />
                        </Group>
                    ) : null}
                    <Table withTableBorder mt="sm">
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Name</Table.Th>
                                <Table.Th>Type</Table.Th>
                                <Table.Th>Sync</Table.Th>
                                <Table.Th>Updated</Table.Th>
                                <Table.Th>Actions</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {paginatedEntities.map((row) => (
                                <Table.Tr key={row.entityId}>
                                    <Table.Td>{row.name}</Table.Td>
                                    <Table.Td>{row.entityType}</Table.Td>
                                    <Table.Td>{row.syncStatus}</Table.Td>
                                    <Table.Td>{row.updatedAt ? DateTime.fromSeconds(row.updatedAt).toFormat('yyyy-LL-dd HH:mm') : '-'}</Table.Td>
                                    <Table.Td>
                                        <Group gap="xs">
                                            <Button size="compact-xs" variant="light"  disabled={!hasUserSync} onClick={() => refreshMutation.mutate(row)}>Refresh</Button>
                                            {moduleData?.isOwner ? (
                                                <Button
                                                    size="compact-xs"
                                                    color="red"
                                                    variant="light"
                                                    onClick={() => {
                                                        if (confirm(`Remove tracked entity ${row.name}?`)) {
                                                            deleteEntityMutation.mutate(row);
                                                        }
                                                    }}
                                                >
                                                    Remove
                                                </Button>
                                            ) : null}
                                        </Group>
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                            {entities.length === 0 ? (
                                <Table.Tr><Table.Td colSpan={5}><Text c="dimmed">No tracked entities.</Text></Table.Td></Table.Tr>
                            ) : null}
                        </Table.Tbody>
                    </Table>
                </Paper>

                {moduleData?.isOwner ? (
                    <>
                        <Paper withBorder p="md" style={{order: 2}}>
                            <Text fw={700}>Auto-discover and Import</Text>
                            <Text size="sm" c="dimmed">Discover entities from your personal inventory using exact filters, then import them.</Text>
                            <Group mt="sm" align="end">
                                <Select
                                    style={{width: 220}}
                                    label="Mode"
                                    value={discoverMode}
                                    onChange={(value) => setDiscoverMode((value as DiscoverMode) || 'all')}
                                    data={[
                                        {value: 'all', label: 'All (type restricted)'},
                                        {value: 'tag', label: 'Exact tag'},
                                        {value: 'infotext', label: 'Exact infotext'},
                                    ]}
                                    allowDeselect={false}
                                />
                                <TextInput
                                    style={{flex: 1}}
                                    label={
                                        discoverMode === 'all'
                                            ? 'Filter value'
                                            : discoverMode === 'tag'
                                                ? 'Tag (exact)'
                                                : 'Infotext (exact)'
                                    }
                                    placeholder={
                                        discoverMode === 'all'
                                            ? 'Not required for ALL mode'
                                            : discoverMode === 'tag'
                                                ? 'Exact tag value'
                                                : 'Exact infotext value'
                                    }
                                    value={discoverValue}
                                    onChange={(event) => setDiscoverValue(event.currentTarget.value)}
                                    disabled={discoverMode === 'all'}
                                />
                            </Group>
                            <Group mt="sm">
                                <Button
                                    loading={discoverStationsMutation.isPending}
                                    disabled={(discoverMode !== 'all' && discoverValue.trim() === '') || !hasUserSync}
                                    onClick={() => discoverStationsMutation.mutate()}
                                >
                                    Discover Stations
                                </Button>
                                <Button
                                    loading={discoverFacilitiesMutation.isPending}
                                    disabled={(discoverMode !== 'all' && discoverValue.trim() === '') || !hasUserSync}
                                    onClick={() => discoverFacilitiesMutation.mutate()}
                                >
                                    Discover Facilities
                                </Button>
                            </Group>
                        </Paper>

                        <Paper withBorder p="md" style={{order: 2}}>
                            <Text fw={700}>Import Entities</Text>
                            <Group mt="sm" align="end">
                                <TextInput
                                    style={{flex: 1}}
                                    label="Station IDs"
                                    placeholder="5:12345, 5:67890"
                                    value={stationIds}
                                    onChange={(event) => setStationIds(event.currentTarget.value)}
                                />
                                <Button
                                  loading={importStationsMutation.isPending}
                                  disabled={!hasUserSync}
                                  onClick={() => importStationsMutation.mutate()}
                                >
                                    Import Stations
                                </Button>
                            </Group>
                            <Group mt="sm" align="end">
                                <TextInput
                                    style={{flex: 1}}
                                    label="Facility IDs"
                                    placeholder="4:12345, 4:67890"
                                    value={facilityIds}
                                    onChange={(event) => setFacilityIds(event.currentTarget.value)}
                                />
                                <Button
                                  loading={importFacilitiesMutation.isPending}
                                  disabled={!hasUserSync}
                                  onClick={() => importFacilitiesMutation.mutate()}
                                >
                                    Import Facilities
                                </Button>
                            </Group>
                        </Paper>

                        {moduleData.settings['syncUserId'] === null && (
                            <Alert color="red" icon={<WarningIcon />} title="Sync user required" style={{order: 1}}>
                                No sync user is configured. Open the Settings tab to select a user that has granted the
                                required API permissions, otherwise imports and refreshes are disabled.
                            </Alert>
                        )}

                        <Paper withBorder p="md" style={{order: 2}}>
                            <Text fw={700}>Groups</Text>
                            <Group mt="sm" align="end">
                                <TextInput
                                    style={{flex: 1}}
                                    label="Group Name"
                                    value={groupName}
                                    onChange={(event) => setGroupName(event.currentTarget.value)}
                                />
                                <MultiSelect
                                    style={{flex: 2}}
                                    label="Entities"
                                    data={trackedEntityOptions}
                                    value={groupEntityIds}
                                    onChange={setGroupEntityIds}
                                    searchable
                                />
                            </Group>
                            <Group mt="sm" align="end">
                                <MultiSelect
                                    style={{flex: 1}}
                                    label="Cargo Holders (optional)"
                                    data={trackedEntityOptions}
                                    value={groupCargoEntityIds}
                                    onChange={setGroupCargoEntityIds}
                                    searchable
                                />
                                <Button
                                    loading={createGroupMutation.isPending}
                                    disabled={groupName.trim() === '' || groupEntityIds.length === 0}
                                    onClick={() => createGroupMutation.mutate()}
                                >
                                    Create Group
                                </Button>
                            </Group>

                            <Table withTableBorder mt="sm">
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th>Name</Table.Th>
                                        <Table.Th>Type</Table.Th>
                                        <Table.Th>Entities</Table.Th>
                                        <Table.Th>Cargo Holders</Table.Th>
                                        <Table.Th>Actions</Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {groups.map((group) => (
                                        <Table.Tr key={group.id}>
                                            <Table.Td>{group.name}</Table.Td>
                                            <Table.Td>{group.entityType}</Table.Td>
                                            <Table.Td>{group.entityIds.length}</Table.Td>
                                            <Table.Td>{group.cargoEntityIds.length}</Table.Td>
                                            <Table.Td>
                                                <Group gap="xs">
                                                    <Button
                                                        size="compact-xs"
                                                        variant="light"
                                                        onClick={() => startEditingGroup(group)}
                                                    >
                                                        Edit
                                                    </Button>
                                                    <Button
                                                        size="compact-xs"
                                                        color="red"
                                                        variant="light"
                                                        onClick={() => {
                                                            if (confirm(`Delete group ${group.name}?`)) {
                                                                deleteGroupMutation.mutate(group.id);
                                                            }
                                                        }}
                                                    >
                                                        Delete
                                                    </Button>
                                                </Group>
                                            </Table.Td>
                                        </Table.Tr>
                                    ))}
                                    {groups.length === 0 ? (
                                        <Table.Tr><Table.Td colSpan={5}><Text c="dimmed">No groups defined.</Text></Table.Td></Table.Tr>
                                    ) : null}
                                </Table.Tbody>
                            </Table>

                            {editingGroupId ? (
                                <Paper withBorder p="md" mt="sm">
                                    <Text fw={700}>Edit Group</Text>
                                    <Group mt="sm" align="end">
                                        <TextInput
                                            style={{flex: 1}}
                                            label="Group Name"
                                            value={editGroupName}
                                            onChange={(event) => setEditGroupName(event.currentTarget.value)}
                                        />
                                        <MultiSelect
                                            style={{flex: 2}}
                                            label="Entities"
                                            data={trackedEntityOptions}
                                            value={editGroupEntityIds}
                                            onChange={setEditGroupEntityIds}
                                            searchable
                                        />
                                    </Group>
                                    <Group mt="sm" align="end">
                                        <MultiSelect
                                            style={{flex: 1}}
                                            label="Cargo Holders (optional)"
                                            data={trackedEntityOptions}
                                            value={editGroupCargoEntityIds}
                                            onChange={setEditGroupCargoEntityIds}
                                            searchable
                                        />
                                        <Button
                                            loading={updateGroupMutation.isPending}
                                            disabled={editGroupName.trim() === '' || editGroupEntityIds.length === 0}
                                            onClick={() => updateGroupMutation.mutate()}
                                        >
                                            Save Group
                                        </Button>
                                        <Button variant="subtle" onClick={stopEditingGroup}>Cancel</Button>
                                    </Group>
                                </Paper>
                            ) : null}
                        </Paper>
                    </>
                ) : (
                    <Alert color="blue" title="Read-only">
                        You can view and refresh entities but only the module owner can modify settings, imports, and groups.
                    </Alert>
                )}
                </Stack>
            </Stack>
        </Page>
    );
}

export default withRouteErrorBoundary(ProductionModuleEntitiesPage);
